/* eslint-disable jsx-a11y/alt-text */
import React from 'react'
import cn from 'classnames'
import Image from 'next/image'

import emptyProfilePicture from './empty-profile-picture.svg'

import styles from './Avatars.module.css'

const sizes = {
  small: 26,
  medium: 34,
  large: 42,
}

const stringToHslColor = (str: string, s = 40, l = 70) => {
  let hash = 0
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < str.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }

  const h = hash % 360
  return `hsl(${h}, ${s}%, ${l}%)`
}

export type InitialsProps = {
  name: string
  className?: string
  size?: 'small' | 'medium' | 'large'
}

const getInitials = (name: string): string | undefined => {
  if (!name) return undefined

  const firstNameInitial = name.slice(0, 1)
  const allNames = name.split(' ')
  const lastNameInitial =
    allNames.length > 1 ? allNames.slice(-1)[0].slice(0, 1) : ''

  return `${firstNameInitial} ${lastNameInitial}`.toUpperCase()
}

const Initials: React.FC<InitialsProps> = ({ name, className, size }) => {
  if (!name) return null

  const initials = getInitials(name)
  const color = stringToHslColor(name)

  return (
    <div
      className={cn(styles.initialsContainer, className, {
        [styles[size]]: size,
      })}
      style={{ backgroundColor: color }}
    >
      <div className={styles.initialsText}>{initials}</div>
    </div>
  )
}

export type SingleAvatarProps = {
  size?: 'small' | 'medium' | 'large'
  pictureUrl?: string
  negativeMargin?: boolean
  name?: string
  className?: string
}
const SingleAvatar: React.FC<SingleAvatarProps> = ({
  size = 'small',
  pictureUrl,
  negativeMargin = false,
  name,
  className,
}) => {
  const sizePx = sizes[size] || 26

  const imageProps = {
    className: cn(styles.avatar, className, {
      [styles.avatarNegativeMargin]: negativeMargin,
    }),
    width: sizePx,
    height: sizePx,
    alt: 'Profile',
  }

  if (pictureUrl) {
    return <Image src={pictureUrl} {...imageProps} />
  }

  if (name) {
    return <Initials name={name} size={size} className={className} />
  }

  return <Image src={emptyProfilePicture} {...imageProps} />
}

export type GroupAvatarsProps = {
  size?: 'small' | 'medium' | 'large'
  pictureUrls: string[]
  label?: string
}
const GroupAvatars: React.FC<GroupAvatarsProps> = ({
  size = 'small',
  pictureUrls,
  label,
}) => (
  <div className={styles.groupAvatarsContainer}>
    {(pictureUrls || []).map((pictureUrl, i) => {
      const key = pictureUrl + i
      if (i < 4) {
        return (
          <SingleAvatar
            key={key}
            size={size}
            pictureUrl={pictureUrl}
            negativeMargin
          />
        )
      }
      return null
    })}
    <div className={styles.groupAvatarsText}>{label || ''}</div>
  </div>
)

export type ProfilePictureProps = {
  pictureUrl?: string
}
const ProfilePicture: React.FC<ProfilePictureProps> = ({ pictureUrl }) =>
  pictureUrl ? (
    <Image
      className={styles.profilePicture}
      src={pictureUrl}
      width={144}
      height={144}
      alt="Profile"
    />
  ) : (
    <div className={styles.emptyProfilePictureContainer}>
      <Image
        src={emptyProfilePicture}
        width={45}
        height={45}
        alt="Empty Profile"
      />
    </div>
  )

export type ProfilePictureAndNameProps = {
  pictureUrl?: string
  name?: string
  title?: string
  textColor?: 'white' | 'dark'
  className?: string
  size?: number
}
const ProfilePictureAndName: React.FC<ProfilePictureAndNameProps> = ({
  pictureUrl,
  name,
  title,
  textColor = 'white',
  className,
  size,
}) => (
  <div className={cn(styles.pictureAndNameContainer, className)}>
    {pictureUrl ? (
      <div className={styles.profilePictureAndName}>
        <Image
          src={pictureUrl}
          width={size ?? 48}
          height={size ?? 48}
          alt="Profile"
        />
      </div>
    ) : (
      <div className={styles.ProfilePictureAndNameEmptyContainer}>
        <Image
          src={emptyProfilePicture}
          width={15}
          height={15}
          alt="Empty Profile"
        />
      </div>
    )}
    <div className={styles.pictureNameSection}>
      <div className={cn(styles.pictureName, styles[`name-${textColor}`])}>
        {name || ''}
      </div>
      <div className={cn(styles.pictureTitle, styles[`title-${textColor}`])}>
        {title || ''}
      </div>
    </div>
  </div>
)

export type ChatAvatarsProps = {
  size?: 'medium' | 'large'
  pictureUrls: string[]
}
const ChatAvatars: React.FC<ChatAvatarsProps> = ({
  size = 'medium',
  pictureUrls,
}) => (
  <div className={styles.groupAvatarsContainer}>
    {(pictureUrls || []).map((pictureUrl, i) => {
      const key = pictureUrl + i
      if (i === 0) {
        return <SingleAvatar key={key} size={size} pictureUrl={pictureUrl} />
      }
      if (i < 4) {
        return (
          <div key={key} className={styles.chatAvatarsPicture}>
            <SingleAvatar pictureUrl={pictureUrl} negativeMargin />
          </div>
        )
      }
      return null
    })}
  </div>
)

export {
  SingleAvatar,
  GroupAvatars,
  ProfilePicture,
  ProfilePictureAndName,
  ChatAvatars,
  Initials,
}
